import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { LogOut, Clock, MapPin, Users, Award, CalendarDays, Edit, CheckCircle, XCircle, FileText, Eye, Upload, Image as ImageIcon, Plus, Camera, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCORE_PARAMS = [
  { key: "personality", label: "Personality" },
  { key: "communication", label: "Communication" },
  { key: "punctuality", label: "Punctuality" },
  { key: "eager_to_learn", label: "Eager to Learn" },
  { key: "work_results", label: "Work Results" }
];

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const [teacher, setTeacher] = useState(null);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedCourseType, setSelectedCourseType] = useState(null);
  const [studentsByCourse, setStudentsByCourse] = useState({});
  const [students, setStudents] = useState([]);
  const [teacherRoles, setTeacherRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationStatus, setLocationStatus] = useState("");
  const [scoreDialog, setScoreDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [scores, setScores] = useState({
    personality: 0,
    communication: 0,
    punctuality: 0,
    eager_to_learn: 0,
    work_results: 0
  });
  const [existingScores, setExistingScores] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loadingScores, setLoadingScores] = useState(false);
  const [isAbsent, setIsAbsent] = useState(false);
  
  // Task Management States
  const [taskDialog, setTaskDialog] = useState(false);
  const [taskForm, setTaskForm] = useState({ course_type: "", batch_number: "", task_description: "", due_date: "" });
  const [submissionsDialog, setSubmissionsDialog] = useState(false);
  const [taskSubmissions, setTaskSubmissions] = useState([]);
  const [viewingSubmission, setViewingSubmission] = useState(null);
  const [studentTaskSubmissions, setStudentTaskSubmissions] = useState({}); // student_id -> submissions
  
  // Extra Work States
  const [extraWorkDialog, setExtraWorkDialog] = useState(false);
  const [extraWorkTab, setExtraWorkTab] = useState("allotted"); // "allotted" or "add_new"
  const [extraWorkForm, setExtraWorkForm] = useState({
    date: "",
    client_name: "",
    location: "",
    service: "",
    vanity_type: "",
    qty: 1
  });
  const [allottedWork, setAllottedWork] = useState([]);
  const [selfCreatedWork, setSelfCreatedWork] = useState([]);
  const [editWorkDialog, setEditWorkDialog] = useState(false);
  const [selectedWork, setSelectedWork] = useState(null);
  const [extraWorkEntries, setExtraWorkEntries] = useState([]);
  
  // Practice Kit States
  const [kitDialog, setKitDialog] = useState(false);
  const [selectedStudentForKit, setSelectedStudentForKit] = useState(null);
  const [kitForm, setKitForm] = useState({
    kit_name: "",
    issue_date: new Date().toISOString().split('T')[0],
    kit_image: null
  });
  const [studentKits, setStudentKits] = useState({});  // student_id -> kits
  const [cameraStream, setCameraStream] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const teacherData = localStorage.getItem("teacher");
    if (!teacherData) {
      navigate("/");
      return;
    }
    
    const parsedTeacher = JSON.parse(teacherData);
    setTeacher(parsedTeacher);
    fetchBatches();
    
    // Load extra work with parsed teacher ID
    loadExtraWorkForTeacher(parsedTeacher.id);
    setLoading(false);
  }, []);

  const fetchBatches = async () => {
    try {
      const response = await axios.get(`${API}/teacher/batches`);
      setBatches(response.data.batches);
    } catch (error) {
      toast.error("Failed to fetch batches");
    }
  };

  const fetchStudentsByBatch = async (batchNumber) => {
    try {
      const teacherName = teacher?.name || "";
      const response = await axios.get(`${API}/teacher/students/${batchNumber}/${teacherName}`);
      setStudentsByCourse(response.data.students_by_course || {});
      setTeacherRoles(response.data.teacher_roles || []);
      setSelectedBatch(batchNumber);
      
      // Load task submissions and kits for all students in this batch
      const allStudents = Object.values(response.data.students_by_course || {}).flat();
      for (const student of allStudents) {
        loadStudentTaskSubmissions(student.id);
        loadStudentKits(student.id);
      }
      
      // If teacher has only one role/course type, auto-select it
      const courseTypes = Object.keys(response.data.students_by_course || {});
      if (courseTypes.length === 1) {
        setSelectedCourseType(courseTypes[0]);
        setStudents(response.data.students_by_course[courseTypes[0]]);
      } else {
        setSelectedCourseType(null);
        setStudents([]);
      }
    } catch (error) {
      toast.error("Failed to fetch students");
    }
  };

  const selectCourseType = (courseType) => {
    setSelectedCourseType(courseType);
    setStudents(studentsByCourse[courseType] || []);
  };

  const handleLogout = () => {
    localStorage.removeItem("teacher");
    navigate("/");
  };

  const markAttendance = (action) => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setLocationStatus(`Marking ${action}...`);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await axios.post(`${API}/attendance/mark`, {
            user_id: teacher.id,
            user_type: "teacher",
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            action: action
          });
          
          toast.success(response.data.message);
          setLocationStatus("");
          
          // Update teacher data
          const updatedTeacher = { ...teacher };
          if (action === "time_in") {
            updatedTeacher.today_time_in = response.data.time;
          } else {
            updatedTeacher.today_time_out = response.data.time;
          }
          setTeacher(updatedTeacher);
          localStorage.setItem("teacher", JSON.stringify(updatedTeacher));
        } catch (error) {
          toast.error(error.response?.data?.detail || "Failed to mark attendance");
          setLocationStatus("");
        }
      },
      (error) => {
        toast.error("Please enable location access");
        setLocationStatus("");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const openScoreDialog = async (student) => {
    setSelectedStudent(student);
    const today = new Date();
    setSelectedDate(today);
    
    // Reset states to default (edit mode with 0 scores)
    setIsEditMode(true);
    setExistingScores(null);
    setIsAbsent(false);
    setScores({
      personality: 0,
      communication: 0,
      punctuality: 0,
      eager_to_learn: 0,
      work_results: 0
    });
    
    // Check if scores already exist for today (this will override if needed)
    await checkExistingScores(student.id, today);
    
    setScoreDialog(true);
  };

  const checkExistingScores = async (studentId, date) => {
    // CRITICAL: Clear ALL state immediately before any async operation
    setLoadingScores(true);
    setExistingScores(null);
    setIsAbsent(false);
    setScores({
      personality: 0,
      communication: 0,
      punctuality: 0,
      eager_to_learn: 0,
      work_results: 0
    });
    setIsEditMode(true);
    
    try {
      const dateStr = date.toISOString().split('T')[0];
      const response = await axios.get(`${API}/scores/${studentId}/${dateStr}`);
      
      if (response.data.exists) {
        // Scores exist - load them and set to read-only
        setExistingScores(response.data);
        setIsAbsent(response.data.is_absent || false);
        
        if (response.data.is_absent) {
          // Student was marked absent
          setScores({
            personality: 0,
            communication: 0,
            punctuality: 0,
            eager_to_learn: 0,
            work_results: 0
          });
        } else {
          setScores(response.data.scores);
        }
        setIsEditMode(false);
      } else {
        // No existing scores - enable edit mode
        setExistingScores(null);
        setIsAbsent(false);
        setScores({
          personality: 0,
          communication: 0,
          punctuality: 0,
          eager_to_learn: 0,
          work_results: 0
        });
        setIsEditMode(true);
      }
    } catch (error) {
      console.error("Failed to check existing scores:", error);
      // If error, assume no existing scores and reset to 0
      setExistingScores(null);
      setIsAbsent(false);
      setScores({
        personality: 0,
        communication: 0,
        punctuality: 0,
        eager_to_learn: 0,
        work_results: 0
      });
      setIsEditMode(true);
    }
    setLoadingScores(false);
  };

  const handleDateChange = async (date) => {
    setSelectedDate(date);
    
    // checkExistingScores will handle all state clearing
    if (selectedStudent) {
      await checkExistingScores(selectedStudent.id, date);
    }
  };

  const handleSubmitScores = async () => {
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      await axios.post(`${API}/scores/submit`, {
        student_id: selectedStudent.id,
        teacher_id: teacher.id,
        scores: scores,
        date: dateStr,
        is_absent: false
      });
      
      toast.success(`Scores submitted and locked for ${dateStr}!`);
      
      // Lock the scores immediately after submission
      setExistingScores({
        scores: scores,
        is_absent: false,
        date: dateStr
      });
      setIsEditMode(false);
      
      // Close the dialog after a brief delay to show the locked state
      setTimeout(() => {
        setScoreDialog(false);
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit scores");
    }
  };

  const handleMarkAbsent = async () => {
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      await axios.post(`${API}/scores/submit`, {
        student_id: selectedStudent.id,
        teacher_id: teacher.id,
        scores: {},
        date: dateStr,
        is_absent: true
      });
      
      toast.success(`${selectedStudent.name} marked as absent and locked for ${dateStr}`);
      
      // Lock the absent status immediately
      setExistingScores({
        scores: {},
        is_absent: true,
        date: dateStr
      });
      setIsEditMode(false);
      
      // Close the dialog after a brief delay
      setTimeout(() => {
        setScoreDialog(false);
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark absent");
    }
  };

  // Task Management Functions
  const handleCreateTask = async () => {
    if (!taskForm.course_type || !taskForm.batch_number || !taskForm.task_description || !taskForm.due_date) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      await axios.post(`${API}/teacher/task/create`, {
        course_type: taskForm.course_type,
        batch_number: taskForm.batch_number,
        due_date: taskForm.due_date,
        task_description: taskForm.task_description,
        teacher_id: teacher.id
      });

      toast.success("Task created successfully!");
      setTaskDialog(false);
      setTaskForm({ course_type: "", batch_number: "", task_description: "", due_date: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create task");
    }
  };

  const loadTaskSubmissions = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API}/teacher/task-submissions/${teacher.id}/${today}`);
      setTaskSubmissions(response.data.submissions || []);
      setSubmissionsDialog(true);
    } catch (error) {
      toast.error("Failed to load task submissions");
    }
  };

  const handleApproveTask = async (submissionId) => {
    try {
      await axios.put(`${API}/teacher/task/${submissionId}/approve`, null, {
        params: { teacher_id: teacher.id }
      });
      
      toast.success("Task approved successfully!");
      
      // Reload submissions for all students
      const allStudents = Object.values(studentsByCourse).flat();
      for (const student of allStudents) {
        loadStudentTaskSubmissions(student.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve task");
    }
  };

  const handleRejectTask = async (submissionId) => {
    try {
      await axios.put(`${API}/teacher/task/${submissionId}/reject`, null, {
        params: { teacher_id: teacher.id }
      });
      
      toast.success("Task rejected");
      
      // Reload submissions for all students
      const allStudents = Object.values(studentsByCourse).flat();
      for (const student of allStudents) {
        loadStudentTaskSubmissions(student.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject task");
    }
  };

  const loadStudentTaskSubmissions = async (studentId) => {
    try {
      const response = await axios.get(`${API}/teacher/task-submissions/${studentId}`);
      setStudentTaskSubmissions(prev => ({
        ...prev,
        [studentId]: response.data.submissions || []
      }));
    } catch (error) {
      console.error("Failed to load student task submissions:", error);
    }
  };

  // Practice Kit Management Functions
  const loadStudentKits = async (studentId) => {
    try {
      const response = await axios.get(`${API}/student/${studentId}/kits`);
      setStudentKits(prev => ({
        ...prev,
        [studentId]: response.data.kits || []
      }));
    } catch (error) {
      console.error("Failed to load student kits:", error);
    }
  };

  const openIssueKitDialog = (student) => {
    setSelectedStudentForKit(student);
    setKitForm({
      kit_name: "",
      issue_date: new Date().toISOString().split('T')[0],
      kit_image: null
    });
    stopCamera(); // Ensure camera is stopped
    setKitDialog(true);
  };

  const handleKitImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB limit for frontend)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("Image too large. Maximum size is 5MB");
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setKitForm(prev => ({ ...prev, kit_image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleIssueKit = async () => {
    if (!kitForm.kit_name || !kitForm.issue_date) {
      toast.error("Please fill in kit name and issue date");
      return;
    }

    if (!kitForm.kit_image) {
      toast.error("Please upload a kit image");
      return;
    }

    try {
      await axios.post(`${API}/teacher/kit/issue`, {
        student_id: selectedStudentForKit.id,
        kit_name: kitForm.kit_name,
        issue_date: kitForm.issue_date,
        issued_by: teacher.id,
        kit_image_base64: kitForm.kit_image
      });

      toast.success("Practice kit issued successfully!");
      setKitDialog(false);
      
      // Reload kits for this student
      loadStudentKits(selectedStudentForKit.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to issue practice kit");
    }
  };

  const handleReturnKit = async (kitId, studentId) => {
    try {
      await axios.put(`${API}/teacher/kit/${kitId}/return`);
      toast.success("Kit marked as returned!");
      
      // Reload kits for this student
      loadStudentKits(studentId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark kit as returned");
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      setCameraStream(stream);
      setShowCamera(true);
      
      // Wait for video element to be available
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      toast.error("Unable to access camera. Please check permissions.");
      console.error("Camera error:", error);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw video frame to canvas
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Convert to base64
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      
      // Check file size
      const sizeInBytes = (imageData.length * 3) / 4;
      const sizeInMB = sizeInBytes / (1024 * 1024);
      
      if (sizeInMB > 5) {
        toast.error("Captured image is too large. Please try again with better lighting.");
        return;
      }
      
      setKitForm(prev => ({ ...prev, kit_image: imageData }));
      stopCamera();
      toast.success("Photo captured!");
    }
  };

  // Extra Work Functions
  const loadExtraWorkForTeacher = async (teacherId) => {
    try {
      const response = await axios.get(`${API}/teacher/extra-work/${teacherId}`);
      setAllottedWork(response.data.allotted_work || []);
      setSelfCreatedWork(response.data.self_created_work || []);
      setExtraWorkEntries(response.data.all_work || []); // For backward compatibility
    } catch (error) {
      console.error("Failed to load extra work:", error);
    }
  };

  const loadExtraWork = async () => {
    if (teacher && teacher.id) {
      await loadExtraWorkForTeacher(teacher.id);
    }
  };

  const handleEditExtraWork = async () => {
    try {
      await axios.put(`${API}/admin/extra-work/${selectedWork.id}/edit`, extraWorkForm);
      toast.success("Extra work updated successfully!");
      setEditWorkDialog(false);
      loadExtraWork();
      setSelectedWork(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update extra work");
    }
  };

  const handleAddExtraWork = async () => {
    if (!extraWorkForm.date || !extraWorkForm.client_name || !extraWorkForm.location || 
        !extraWorkForm.service || !extraWorkForm.vanity_type || !extraWorkForm.qty) {
      toast.error("Please fill all fields");
      return;
    }

    try {
      await axios.post(`${API}/teacher/extra-work/add`, {
        ...extraWorkForm,
        teacher_id: teacher.id
      });

      toast.success("Extra work added successfully! Pending admin approval.");
      setExtraWorkDialog(false);
      setExtraWorkForm({
        date: "",
        client_name: "",
        location: "",
        service: "",
        vanity_type: "",
        qty: 1
      });
      loadExtraWork(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add extra work");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Welcome, {teacher?.name}!</h1>
            <p className="text-gray-600 mt-1">Teacher Dashboard</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleLogout} data-testid="teacher-logout-button">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Attendance Card */}
        <Card className="mb-6 border-2 border-green-200 bg-white/80 backdrop-blur-sm" data-testid="teacher-attendance-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Today's Attendance
            </CardTitle>
            <CardDescription>Mark your time in and time out (within 50m of academy)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">Time In</p>
                {teacher?.today_time_in ? (
                  <p className="font-semibold text-green-700">
                    {new Date(teacher.today_time_in).toLocaleTimeString()}
                  </p>
                ) : (
                  <Button
                    onClick={() => markAttendance("time_in")}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600"
                    disabled={locationStatus !== ""}
                    data-testid="teacher-time-in-button"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    {locationStatus === "Marking time_in..." ? "Marking..." : "Mark Time In"}
                  </Button>
                )}
              </div>
              
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">Time Out</p>
                {teacher?.today_time_out ? (
                  <p className="font-semibold text-red-700">
                    {new Date(teacher.today_time_out).toLocaleTimeString()}
                  </p>
                ) : (
                  <Button
                    onClick={() => markAttendance("time_out")}
                    className="w-full bg-gradient-to-r from-red-500 to-rose-600"
                    disabled={!teacher?.today_time_in || locationStatus !== ""}
                    data-testid="teacher-time-out-button"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    {locationStatus === "Marking time_out..." ? "Marking..." : "Mark Time Out"}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Batch Selection */}
        {!selectedBatch && (
          <Card className="bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Select Batch
              </CardTitle>
              <CardDescription>Choose a batch to view students and submit scores</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
                {batches.map((batch, idx) => (
                  <Card 
                    key={batch} 
                    className="border-2 hover:border-green-500 cursor-pointer transition-all hover:shadow-lg"
                    onClick={() => fetchStudentsByBatch(batch)}
                  >
                    <CardContent className="p-6 text-center">
                      <p className="text-2xl font-bold text-green-600">Batch {batch}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {batches.length === 0 && (
                <p className="text-center py-8 text-gray-500">No batches available</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Extra Work Section - Below Batch Selection */}
        <Card className="bg-white/90 backdrop-blur-sm mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-600" />
              Extra Work Management
            </CardTitle>
            <CardDescription>View allotted work and add your own extra work entries</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={extraWorkTab} onValueChange={setExtraWorkTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="allotted">Allotted Work ({allottedWork.length})</TabsTrigger>
                <TabsTrigger value="add_new">My Work ({selfCreatedWork.length})</TabsTrigger>
              </TabsList>

              {/* Allotted Work Tab */}
              <TabsContent value="allotted">
                {allottedWork.length > 0 ? (
                  <div className="space-y-3">
                    {allottedWork.map((work, idx) => (
                      <Card key={idx} className="border-purple-200">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                  Allotted by Admin
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  work.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {work.status === 'approved' ? `Approved - ₹${work.amount}` : 'Pending Approval'}
                                </span>
                              </div>
                              <h4 className="font-semibold">{work.client_name}</h4>
                              <p className="text-sm text-gray-600">Date: {new Date(work.date).toLocaleDateString()}</p>
                              <p className="text-sm text-gray-600">Service: {work.service} | {work.vanity_type}</p>
                              <p className="text-sm text-gray-600">Location: {work.location} | Qty: {work.qty}</p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedWork(work);
                                setExtraWorkForm({
                                  date: work.date,
                                  client_name: work.client_name,
                                  location: work.location,
                                  service: work.service,
                                  vanity_type: work.vanity_type,
                                  qty: work.qty
                                });
                                setEditWorkDialog(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Award className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No work allotted by admin yet</p>
                  </div>
                )}
              </TabsContent>

              {/* My Work Tab */}
              <TabsContent value="add_new">
                <div className="space-y-4">
                  <Button 
                    className="w-full bg-purple-500 hover:bg-purple-600" 
                    onClick={() => {
                      setExtraWorkForm({
                        date: "",
                        client_name: "",
                        location: "",
                        service: "",
                        vanity_type: "",
                        qty: 1
                      });
                      setExtraWorkDialog(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add New Extra Work
                  </Button>

                  {selfCreatedWork.length > 0 && (
                    <div className="space-y-3">
                      {selfCreatedWork.map((work, idx) => (
                        <Card key={idx} className="border-blue-200">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                    Self Added
                                  </span>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    work.status === 'approved' ? 'bg-green-100 text-green-700' : 
                                    work.status === 'under_review' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
                                  }`}>
                                    {work.status === 'approved' ? `Approved - ₹${work.amount}` : 
                                     work.status === 'under_review' ? 'Under Review' : work.status}
                                  </span>
                                </div>
                                <h4 className="font-semibold">{work.client_name}</h4>
                                <p className="text-sm text-gray-600">Date: {new Date(work.date).toLocaleDateString()}</p>
                                <p className="text-sm text-gray-600">Service: {work.service} | {work.vanity_type}</p>
                                <p className="text-sm text-gray-600">Location: {work.location} | Qty: {work.qty}</p>
                              </div>
                              {work.status === 'under_review' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedWork(work);
                                    setExtraWorkForm({
                                      date: work.date,
                                      client_name: work.client_name,
                                      location: work.location,
                                      service: work.service,
                                      vanity_type: work.vanity_type,
                                      qty: work.qty
                                    });
                                    setEditWorkDialog(true);
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Course Type Selection - Show only if teacher has multiple roles */}
        {selectedBatch && !selectedCourseType && Object.keys(studentsByCourse).length > 1 && (
          <Card className="bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Batch {selectedBatch} - Select Course Type
                </CardTitle>
                <Button variant="outline" onClick={() => { setSelectedBatch(null); setStudents([]); setSelectedCourseType(null); }}>
                  Back to Batches
                </Button>
              </div>
              <CardDescription>Choose which course students you want to view</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.keys(studentsByCourse).map(courseType => (
                  <Card 
                    key={courseType}
                    className="border-2 hover:border-blue-500 cursor-pointer transition-all hover:shadow-lg"
                    onClick={() => selectCourseType(courseType)}
                  >
                    <CardContent className="p-6 text-center">
                      <div className="text-4xl mb-2">
                        {courseType === "makeup" && "💄"}
                        {courseType === "hair" && "💇"}
                        {courseType === "nails" && "💅"}
                        {courseType === "draping" && "👗"}
                      </div>
                      <p className="text-xl font-bold capitalize">{courseType}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {studentsByCourse[courseType]?.length || 0} students
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Students List */}
        {selectedBatch && selectedCourseType && (
          <Card className="bg-white/90 backdrop-blur-sm" data-testid="students-list-card">
            <CardHeader>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Batch {selectedBatch} - {selectedCourseType.charAt(0).toUpperCase() + selectedCourseType.slice(1)} Students ({students.length})
                  </CardTitle>
                  <CardDescription>Submit daily scores for students and manage tasks</CardDescription>
                </div>
                <div className="flex gap-2">
                  {Object.keys(studentsByCourse).length > 1 && (
                    <Button variant="outline" onClick={() => { setSelectedCourseType(null); setStudents([]); }}>
                      ← Back to Course Types
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => { setSelectedBatch(null); setStudents([]); setSelectedCourseType(null); }}>
                    Back to Batches
                  </Button>
                </div>
              </div>
              
              {/* Task Management Section */}
              <div className="bg-gradient-to-r from-orange-50 to-blue-50 p-4 rounded-lg border-2 border-orange-200">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-orange-600" />
                    Daily Task Management
                  </h3>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      className="bg-orange-500 hover:bg-orange-600" 
                      onClick={() => setTaskDialog(true)}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Create New Task
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="bg-blue-50 border-blue-300" 
                      onClick={loadTaskSubmissions}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View All Submissions
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Create practice tasks for this batch and review student submissions below each student card
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {students.map((student, idx) => {
                  // Check if this student can be scored by the teacher
                  // Use the can_score flag from backend response
                  const canScore = student.can_score === true;
                  
                  return (
                    <Card key={student.id} className="border hover:border-green-400 transition-all" data-testid={`student-card-${idx}`}>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">{student.name}</CardTitle>
                        <CardDescription>
                          Batch: {student.batch_number || "Not Assigned"}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-xl">
                            {student.course_type === "makeup" && "💄"}
                            {student.course_type === "hair" && "💇"}
                            {student.course_type === "nails" && "💅"}
                            {student.course_type === "draping" && "👗"}
                          </span>
                          <div className="flex flex-col">
                            <p className="text-sm text-gray-600 capitalize">
                              {student.course_type} - {student.course_name}
                            </p>
                            {student.course_specialization && (
                              <p className="text-xs text-purple-600 font-medium">
                                {student.course_specialization}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Task Submissions Section */}
                        {studentTaskSubmissions[student.id] && studentTaskSubmissions[student.id].length > 0 && (
                          <div className="mb-3 p-2 bg-orange-50 rounded-lg border">
                            <p className="text-xs font-semibold text-orange-700 mb-2">Task Submissions ({studentTaskSubmissions[student.id].length})</p>
                            <div className="space-y-1 max-h-20 overflow-y-auto">
                              {studentTaskSubmissions[student.id].slice(0, 3).map((submission, subIdx) => (
                                <div key={subIdx} className="flex justify-between items-center text-xs">
                                  <span className="text-gray-700 truncate flex-1" title={submission.task_description}>
                                    {submission.task_description.substring(0, 30)}...
                                  </span>
                                  <div className="flex items-center gap-1">
                                    {submission.is_approved ? (
                                      <span className="text-green-600 font-semibold">✓ Approved</span>
                                    ) : submission.approval_status === 'rejected' ? (
                                      <span className="text-red-600 font-semibold">✗ Rejected</span>
                                    ) : submission.approval_status === 'pending' ? (
                                      <div className="flex gap-1">
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-5 px-2 text-xs bg-green-100 hover:bg-green-200"
                                          onClick={() => handleApproveTask(submission.id)}
                                        >
                                          ✓
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-5 px-2 text-xs bg-red-100 hover:bg-red-200"
                                          onClick={() => handleRejectTask(submission.id)}
                                        >
                                          ✗
                                        </Button>
                                      </div>
                                    ) : (
                                      <span className="text-gray-500 text-xs">Not submitted</span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Practice Kits Section */}
                        <div className="mb-3 p-2 bg-purple-50 rounded-lg border">
                          <div className="flex justify-between items-center mb-2">
                            <p className="text-xs font-semibold text-purple-700">Practice Kits</p>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-xs bg-purple-100 hover:bg-purple-200"
                              onClick={() => openIssueKitDialog(student)}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Issue Kit
                            </Button>
                          </div>
                          {studentKits[student.id] && studentKits[student.id].length > 0 ? (
                            <div className="space-y-1 max-h-24 overflow-y-auto">
                              {studentKits[student.id].map((kit, kitIdx) => (
                                <div key={kitIdx} className="flex justify-between items-center text-xs bg-white p-1 rounded">
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-800 truncate">{kit.kit_name}</p>
                                    <p className="text-gray-500 text-xs">
                                      Issued: {new Date(kit.issue_date).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <div className="ml-2">
                                    {kit.is_returned ? (
                                      <span className="text-green-600 font-semibold text-xs">✓ Returned</span>
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 px-2 text-xs bg-green-100 hover:bg-green-200"
                                        onClick={() => handleReturnKit(kit.id, student.id)}
                                      >
                                        Mark Returned
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500">No kits issued yet</p>
                          )}
                        </div>

                        {canScore ? (
                          <Button
                            onClick={() => openScoreDialog(student)}
                            className="w-full"
                            size="sm"
                            data-testid={`submit-score-button-${idx}`}
                          >
                            <Award className="w-4 h-4 mr-2" />
                            Submit Scores
                          </Button>
                        ) : (
                          <div className="text-center py-2 text-sm text-gray-500 bg-gray-100 rounded">
                            View Only - No Scoring
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
            </div>
            
              {students.length === 0 && (
                <div className="text-center py-12 text-gray-500" data-testid="no-students-message">
                  No students in this batch
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Extra Work Management Section */}
        {extraWorkEntries.length > 0 && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-purple-500" />
                Your Extra Work
              </CardTitle>
              <CardDescription>Track your external work and earnings</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Date</th>
                      <th className="text-left p-2">Client</th>
                      <th className="text-left p-2">Location</th>
                      <th className="text-left p-2">Service</th>
                      <th className="text-left p-2">Vanity Type</th>
                      <th className="text-left p-2">Qty</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-left p-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {extraWorkEntries.map((work, idx) => (
                      <tr key={work.id} className="border-b hover:bg-gray-50">
                        <td className="p-2">{new Date(work.date).toLocaleDateString()}</td>
                        <td className="p-2 font-medium">{work.client_name}</td>
                        <td className="p-2">{work.location}</td>
                        <td className="p-2">{work.service}</td>
                        <td className="p-2">{work.vanity_type}</td>
                        <td className="p-2 text-center">{work.qty}</td>
                        <td className="p-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            work.status === 'approved' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {work.status === 'approved' ? 'Approved' : 'Under Review'}
                          </span>
                        </td>
                        <td className="p-2 font-semibold">
                          {work.status === 'approved' ? `₹${work.amount}` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Summary */}
              <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span>Total Entries: {extraWorkEntries.length}</span>
                  <span>Approved: {extraWorkEntries.filter(w => w.status === 'approved').length}</span>
                  <span className="font-semibold">
                    Total Earnings: ₹{extraWorkEntries
                      .filter(w => w.status === 'approved')
                      .reduce((sum, w) => sum + w.amount, 0)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Score Submission Dialog */}
      <Dialog open={scoreDialog} onOpenChange={setScoreDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="score-dialog">
          <DialogHeader>
            <DialogTitle>
              {existingScores && !isEditMode ? "View Scores" : "Submit Scores"} for {selectedStudent?.name}
            </DialogTitle>
            <DialogDescription>
              {existingScores && !isEditMode 
                ? "Scores already submitted for this date. Click 'Edit Marks' to modify." 
                : "Select date and rate on a scale of 1-10"}
            </DialogDescription>
          </DialogHeader>
          
          {loadingScores ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Calendar */}
              <div className="flex justify-center border rounded-lg p-4 bg-gray-50">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateChange}
                  disabled={(date) => date > new Date()}
                  className="rounded-md"
                  data-testid="score-calendar"
                />
              </div>
              
              <div className="text-center p-3 bg-blue-50 rounded">
                <p className="text-sm text-gray-600">Selected Date</p>
                <p className="font-bold text-lg">{selectedDate?.toLocaleDateString()}</p>
                {loadingScores && (
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                    <span className="animate-spin">⏳</span>
                    <span>Checking scores...</span>
                  </div>
                )}
                {!loadingScores && existingScores && !isAbsent && (
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                    <CheckCircle className="w-4 h-4" />
                    <span>Scores Already Submitted</span>
                  </div>
                )}
                {!loadingScores && isAbsent && (
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                    <XCircle className="w-4 h-4" />
                    <span>Marked as Absent</span>
                  </div>
                )}
                {!loadingScores && !existingScores && !isAbsent && (
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                    <span>✏️</span>
                    <span>Ready to Submit</span>
                  </div>
                )}
              </div>

              {SCORE_PARAMS.map(param => (
                <div key={param.key}>
                  <Label htmlFor={param.key}>{param.label}</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <Input
                      id={param.key}
                      type="number"
                      min="0"
                      max="10"
                      step="0.5"
                      value={scores[param.key]}
                      onChange={(e) => setScores({
                        ...scores,
                        [param.key]: Math.min(10, Math.max(0, parseFloat(e.target.value) || 0))
                      })}
                      className="w-24"
                      disabled={!isEditMode}
                      data-testid={`score-input-${param.key}`}
                    />
                    <Input
                      type="range"
                      min="0"
                      max="10"
                      step="0.5"
                      value={scores[param.key]}
                      onChange={(e) => setScores({
                        ...scores,
                        [param.key]: parseFloat(e.target.value)
                      })}
                      className="flex-1"
                      disabled={!isEditMode}
                    />
                  </div>
                </div>
              ))}
              
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600 mb-2">
                  Total Score: <span className="font-bold text-lg">
                    {Object.values(scores).reduce((a, b) => a + b, 0)}/50
                  </span>
                </p>
              </div>
              
              {/* Show Edit button if scores exist and not in edit mode */}
              {existingScores && !isEditMode ? (
                <Button
                  onClick={() => setIsEditMode(true)}
                  className="w-full bg-gradient-to-r from-blue-500 to-blue-600"
                  data-testid="edit-marks-button"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  {isAbsent ? "Edit Absent Status" : "Edit Marks"}
                </Button>
              ) : (
                <div className="flex gap-3">
                  <Button
                    onClick={handleSubmitScores}
                    className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600"
                    disabled={!isEditMode}
                    data-testid="submit-scores-button"
                  >
                    {existingScores ? "Update Scores" : "Submit Scores"}
                  </Button>
                  <Button
                    onClick={handleMarkAbsent}
                    className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700"
                    disabled={!isEditMode}
                    data-testid="mark-absent-button"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Mark Absent
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog open={taskDialog} onOpenChange={setTaskDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Daily Task</DialogTitle>
            <DialogDescription>Assign a practice task for students today</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Course Type</Label>
              <Select value={taskForm.course_type} onValueChange={(val) => setTaskForm({...taskForm, course_type: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select course type" />
                </SelectTrigger>
                <SelectContent>
                  {teacherRoles.map(role => (
                    <SelectItem key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Batch Number</Label>
              <Select value={taskForm.batch_number} onValueChange={(val) => setTaskForm({...taskForm, batch_number: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select batch" />
                </SelectTrigger>
                <SelectContent>
                  {batches.map(batch => (
                    <SelectItem key={batch} value={batch}>
                      Batch {batch}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Due Date</Label>
              <Input 
                type="date"
                value={taskForm.due_date}
                onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div>
              <Label>Task Description</Label>
              <Textarea 
                value={taskForm.task_description}
                onChange={(e) => setTaskForm({...taskForm, task_description: e.target.value})}
                placeholder="Describe the practice task..."
                rows={4}
              />
            </div>
            
            <Button onClick={handleCreateTask} className="w-full bg-orange-500 hover:bg-orange-600">
              Create Task
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Submissions Dialog */}
      <Dialog open={submissionsDialog} onOpenChange={setSubmissionsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Today's Task Submissions</DialogTitle>
            <DialogDescription>View work submitted by your mentees</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {taskSubmissions.length > 0 ? (
              taskSubmissions.map((submission, idx) => (
                <Card key={idx} className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-semibold">{submission.student_name}</p>
                        <p className="text-sm text-gray-600">{submission.student_mobile}</p>
                        <span className="inline-block mt-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded capitalize">
                          {submission.course_type}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setViewingSubmission(submission)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Files ({submission.submission_files?.length || 0})
                      </Button>
                    </div>
                    <p className="text-sm text-gray-700">Task: {submission.task_description}</p>
                    <p className="text-xs text-gray-500 mt-1">Submitted: {new Date(submission.submitted_at).toLocaleString()}</p>
                  </CardContent>
                </Card>
              ))
            ) : (
              <p className="text-center py-8 text-gray-500">No submissions yet for today</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* View Submission Files Dialog */}
      <Dialog open={viewingSubmission !== null} onOpenChange={() => setViewingSubmission(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Submitted Files - {viewingSubmission?.student_name}</DialogTitle>
            <DialogDescription>
              Task: {viewingSubmission?.task_description}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {viewingSubmission?.submission_files?.map((file, idx) => (
              <div key={idx} className="border rounded-lg p-2">
                {file.startsWith('data:image') ? (
                  <img src={file} alt={`Submission ${idx + 1}`} className="w-full h-auto rounded" />
                ) : (
                  <video src={file} controls className="w-full h-auto rounded" />
                )}
                <p className="text-xs text-gray-500 mt-2 text-center">File {idx + 1}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Extra Work Dialog */}
      <Dialog open={extraWorkDialog} onOpenChange={setExtraWorkDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Extra Work</DialogTitle>
            <DialogDescription>
              Log your external/freelance work for admin approval
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Date</Label>
              <Input 
                type="date"
                value={extraWorkForm.date}
                onChange={(e) => setExtraWorkForm({...extraWorkForm, date: e.target.value})}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div>
              <Label>Client Name</Label>
              <Input 
                value={extraWorkForm.client_name}
                onChange={(e) => setExtraWorkForm({...extraWorkForm, client_name: e.target.value})}
                placeholder="e.g., Wedding Client, Event Company"
              />
            </div>
            
            <div>
              <Label>Location</Label>
              <Input 
                value={extraWorkForm.location}
                onChange={(e) => setExtraWorkForm({...extraWorkForm, location: e.target.value})}
                placeholder="e.g., Hotel Taj, Private Venue"
              />
            </div>
            
            <div>
              <Label>Service</Label>
              <Select value={extraWorkForm.service} onValueChange={(val) => setExtraWorkForm({...extraWorkForm, service: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select service type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bridal Makeup">Bridal Makeup</SelectItem>
                  <SelectItem value="Party Makeup">Party Makeup</SelectItem>
                  <SelectItem value="Event Makeup">Event Makeup</SelectItem>
                  <SelectItem value="Hair Styling">Hair Styling</SelectItem>
                  <SelectItem value="Nail Art">Nail Art</SelectItem>
                  <SelectItem value="Draping">Draping</SelectItem>
                  <SelectItem value="Full Package">Full Package</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Vanity Type</Label>
              <Select value={extraWorkForm.vanity_type} onValueChange={(val) => setExtraWorkForm({...extraWorkForm, vanity_type: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select vanity type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HD Makeup">HD Makeup</SelectItem>
                  <SelectItem value="Traditional Makeup">Traditional Makeup</SelectItem>
                  <SelectItem value="Airbrush Makeup">Airbrush Makeup</SelectItem>
                  <SelectItem value="Natural Look">Natural Look</SelectItem>
                  <SelectItem value="Glamour Look">Glamour Look</SelectItem>
                  <SelectItem value="Vintage Look">Vintage Look</SelectItem>
                  <SelectItem value="Contemporary">Contemporary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Quantity</Label>
              <Input 
                type="number"
                min="1"
                value={extraWorkForm.qty}
                onChange={(e) => setExtraWorkForm({...extraWorkForm, qty: parseInt(e.target.value) || 1})}
              />
            </div>
            
            <Button onClick={handleAddExtraWork} className="w-full bg-purple-500 hover:bg-purple-600">
              Add Extra Work
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Extra Work Dialog */}
      <Dialog open={editWorkDialog} onOpenChange={setEditWorkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Extra Work</DialogTitle>
            <DialogDescription>Update the extra work details</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Date</Label>
              <Input 
                type="date"
                value={extraWorkForm.date}
                onChange={(e) => setExtraWorkForm({...extraWorkForm, date: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Client Name</Label>
              <Input 
                value={extraWorkForm.client_name}
                onChange={(e) => setExtraWorkForm({...extraWorkForm, client_name: e.target.value})}
                placeholder="Enter client name"
              />
            </div>
            
            <div>
              <Label>Location</Label>
              <Input 
                value={extraWorkForm.location}
                onChange={(e) => setExtraWorkForm({...extraWorkForm, location: e.target.value})}
                placeholder="Enter location"
              />
            </div>
            
            <div>
              <Label>Service Type</Label>
              <Input 
                value={extraWorkForm.service}
                onChange={(e) => setExtraWorkForm({...extraWorkForm, service: e.target.value})}
                placeholder="E.g., Bridal, Party, Photoshoot"
              />
            </div>
            
            <div>
              <Label>Vanity Type</Label>
              <Input 
                value={extraWorkForm.vanity_type}
                onChange={(e) => setExtraWorkForm({...extraWorkForm, vanity_type: e.target.value})}
                placeholder="E.g., HD, Airbrush, Regular"
              />
            </div>
            
            <div>
              <Label>Quantity</Label>
              <Input 
                type="number"
                min="1"
                value={extraWorkForm.qty}
                onChange={(e) => setExtraWorkForm({...extraWorkForm, qty: parseInt(e.target.value) || 1})}
              />
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditWorkDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleEditExtraWork} className="flex-1 bg-purple-500 hover:bg-purple-600">
                Update
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Issue Practice Kit Dialog */}
      <Dialog open={kitDialog} onOpenChange={(open) => {
        setKitDialog(open);
        if (!open) {
          stopCamera();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Issue Practice Kit</DialogTitle>
            <DialogDescription>
              Issue a practice kit to {selectedStudentForKit?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Kit Name</Label>
              <Input 
                value={kitForm.kit_name}
                onChange={(e) => setKitForm({...kitForm, kit_name: e.target.value})}
                placeholder="e.g., Basic Makeup Kit, Hair Styling Kit"
              />
            </div>
            
            <div>
              <Label>Issue Date</Label>
              <Input 
                type="date"
                value={kitForm.issue_date}
                onChange={(e) => setKitForm({...kitForm, issue_date: e.target.value})}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            
            <div>
              <Label>Kit Image (Required)</Label>
              
              {!showCamera && !kitForm.kit_image && (
                <div className="flex gap-2 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={startCamera}
                    className="flex-1 border-2 border-purple-300 hover:bg-purple-50"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Take Photo
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('kit-file-input').click()}
                    className="flex-1 border-2 border-blue-300 hover:bg-blue-50"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload File
                  </Button>
                </div>
              )}
              
              <Input 
                id="kit-file-input"
                type="file"
                accept="image/*"
                onChange={handleKitImageUpload}
                className="hidden"
              />
              
              {showCamera && (
                <div className="mt-2 space-y-2">
                  <div className="relative bg-black rounded-lg overflow-hidden">
                    <video 
                      ref={videoRef}
                      autoPlay 
                      playsInline
                      className="w-full h-64 object-cover"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={capturePhoto}
                      className="flex-1 bg-purple-500 hover:bg-purple-600"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Capture Photo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={stopCamera}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              
              {kitForm.kit_image && !showCamera && (
                <div className="mt-2 space-y-2">
                  <img 
                    src={kitForm.kit_image} 
                    alt="Kit preview" 
                    className="w-full h-64 object-cover rounded-lg border-2 border-green-500"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setKitForm({...kitForm, kit_image: null})}
                    className="w-full text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove & Retake
                  </Button>
                </div>
              )}
              
              <canvas ref={canvasRef} className="hidden" />
              
              <p className="text-xs text-gray-500 mt-2">
                Take a photo with camera or upload from gallery (Max 5MB)
              </p>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button 
                type="button"
                variant="outline" 
                onClick={() => {
                  setKitDialog(false);
                  stopCamera();
                }} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="button"
                onClick={handleIssueKit} 
                className="flex-1 bg-purple-500 hover:bg-purple-600"
                disabled={!kitForm.kit_image}
              >
                <Upload className="w-4 h-4 mr-2" />
                Issue Kit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
